import store from '../index';
import { addCentralizedError, showErrorModal, ObservationError } from '../slices/observationSlice';
import { generateErrorId, extractOriginalMessage, ensureRetry } from './errorHandlerBase';

/**
 * API Names Constants for Observation Module
 */
export const OBSERVATION_API_NAMES = {
  FETCH_OBSERVATIONS: 'fetchObservations',
  FETCH_OBSERVATION_DETAILS: 'fetchObservationDetails',
  FETCH_EMPLOYEE_PERFORMANCE: 'fetchEmployeePerformance',
  CREATE_OBSERVATION: 'createObservation',
  UPDATE_OBSERVATION: 'updateObservation',
  DELETE_OBSERVATION: 'deleteObservation',
  FETCH_OBSERVATION_QUESTIONS: 'fetchObservationQuestions',
  SUBMIT_OBSERVATION: 'submitObservation',
  SAVE_OBSERVATION: 'saveObservation',
  UPLOAD_IMAGE: 'uploadImage',
  DELETE_IMAGE: 'deleteImage',
} as const;

/**
 * Helper function to create user-friendly error messages based on API name
 * Following DLO pattern for consistency
 */
export const getUserFriendlyErrorMessage = (apiName: string, originalMessage: string): string => {
  const friendlyMessages: Record<string, string> = {
    [OBSERVATION_API_NAMES.FETCH_OBSERVATIONS]: 'Unable to load observations. Please try again.',
    [OBSERVATION_API_NAMES.FETCH_OBSERVATION_DETAILS]: 'Unable to load observation details. Please try again.',
    [OBSERVATION_API_NAMES.FETCH_EMPLOYEE_PERFORMANCE]: 'Unable to load employee performance data. Please try again.',
    [OBSERVATION_API_NAMES.CREATE_OBSERVATION]: 'Unable to create observation. Please try again.',
    [OBSERVATION_API_NAMES.UPDATE_OBSERVATION]: 'Unable to update observation. Please try again.',
    [OBSERVATION_API_NAMES.DELETE_OBSERVATION]: 'Unable to delete observation. Please try again.',
    [OBSERVATION_API_NAMES.FETCH_OBSERVATION_QUESTIONS]: 'Unable to load observation questions. Please try again.',
    [OBSERVATION_API_NAMES.SUBMIT_OBSERVATION]: 'Unable to submit observation. Please try again.',
    [OBSERVATION_API_NAMES.SAVE_OBSERVATION]: 'Unable to save observation. Please try again.',
    [OBSERVATION_API_NAMES.UPLOAD_IMAGE]: 'Unable to upload image. Please try again.',
    [OBSERVATION_API_NAMES.DELETE_IMAGE]: 'Unable to delete image. Please try again.',
  };

  // DLO pattern: Return generic message with original error details
  return `Unable to fetch the data. ${originalMessage || 'Please try again.'}` || friendlyMessages[apiName];
};

/**
 * Enhanced error handler that creates user-friendly messages
 * and handles both display and logging
 * 
 * @param apiName - The name of the API that failed
 * @param error - The error object or string
 * @param displayModal - Whether to show error modal
 * @param context - Additional context
 * @returns The created ObservationError object
 */
export const handleObservationErrorWithFriendlyMessage = (
  apiName: string,
  error: any,
  displayModal: boolean = true,
  context?: any
): ObservationError => {
  // Get the original error message
  const originalMessage = extractOriginalMessage(error);

  // Get user-friendly message
  const friendlyMessage = getUserFriendlyErrorMessage(apiName, originalMessage);

  // Prefer API response error field when available
  const apiResponseError = error?.response?.data?.error || error?.response?.data?.errors || error?.response?.data?.message;

  // Choose what text should be shown in the overlay vs stored friendly message
  const displayMessage = displayModal ? (apiResponseError || originalMessage || friendlyMessage) : friendlyMessage;

  // Create enhanced error object
  const observationError: ObservationError = {
    id: generateErrorId(),
    apiName,
    message: ensureRetry(displayMessage),
    timestamp: Date.now(),
    context: {
      originalMessage,
      ...context,
      originalError: error,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown'
    }
  };

  // Add to centralized error state
  store.dispatch(addCentralizedError(observationError));

  // Optionally show the error modal
  if (displayModal) {
    store.dispatch(showErrorModal(observationError));
  }

  // Log to console for development
  console.error(`[Observation Error] ${apiName}:`, {
    message: friendlyMessage,
    errorId: observationError.id,
    originalError: error,
    context: observationError.context
  });

  return observationError;
};

/**
 * Wrapper function for async thunks that handles errors gracefully
 * This prevents promise rejections that would bubble up to ErrorBoundary
 * 
 * @param apiCall - The API function to call
 * @param apiName - The name of the API
 * @param displayModal - Whether to show error modal on failure
 * @param context - Additional context for error tracking
 * @returns Promise that always resolves, either with data or null
 */
export const safeApiCall = async <T>(
  apiCall: () => Promise<T>,
  apiName: string,
  displayModal: boolean = true,
  context?: any
): Promise<T | null> => {
  try {
    const result = await apiCall();
    return result;
  } catch (error) {
    handleObservationErrorWithFriendlyMessage(apiName, error, displayModal, context);
    // Return null instead of throwing to prevent ErrorBoundary trigger
    return null;
  }
};
